// frontend/src/pages/Socrates/contexts/ParameterContext.js

import React, { createContext, useContext, useState, useCallback } from 'react';
import parameterService from '../../../services/socrates/parameterService';

const ParameterContext = createContext();

export const useParameter = () => {
  const context = useContext(ParameterContext);
  if (!context) {
    throw new Error('useParameter must be used within a ParameterProvider');
  }
  return context;
};

export const ParameterProvider = ({ children }) => {
  const [availableParameters, setAvailableParameters] = useState([]);
  const [parametersLoaded, setParametersLoaded] = useState(false);
  const [isLoadingParameters, setIsLoadingParameters] = useState(false);

  // Memoized function to fetch available parameters
  const fetchAvailableParameters = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (parametersLoaded || isLoadingParameters) {
      console.log('Parameters already loaded or loading, returning cached data');
      return availableParameters;
    }

    setIsLoadingParameters(true);
    
    try {
      console.log('Fetching available parameters...');
      const params = await parameterService.getParameters();
      console.log('Parameters fetched successfully:', params.length);
      
      setAvailableParameters(params);
      setParametersLoaded(true);
      return params;
    } catch (error) {
      console.error('Error fetching available parameters:', error);
      return [];
    } finally {
      setIsLoadingParameters(false);
    }
  }, [parametersLoaded, isLoadingParameters, availableParameters]);

  // Memoized function to get parameter options
  const getParameterOptions = useCallback(async (parameter) => {
    try {
      return await parameterService.getParameterOptions(parameter);
    } catch (error) {
      console.error('Error generating parameter options:', error);
      return [];
    }
  }, []);

  // Force refresh parameters
  const refreshParameters = useCallback(async () => {
    console.log('Refreshing parameters...');
    setParametersLoaded(false);
    setIsLoadingParameters(false);
    setAvailableParameters([]);
    return await fetchAvailableParameters();
  }, [fetchAvailableParameters]);

  const value = {
    availableParameters,
    parametersLoaded,
    isLoadingParameters,
    fetchAvailableParameters,
    getParameterOptions,
    refreshParameters
  };

  return (
    <ParameterContext.Provider value={value}>
      {children}
    </ParameterContext.Provider>
  );
};